import React from 'react';
import type { BuildProQuote, BuildProParams } from './types';
import { getCatalogue } from './catalogue';

export const QuotePDFRenderer: React.FC<{ quote: BuildProQuote; params: BuildProParams; id: string }> = ({ quote, params, id }) => {
  const cats = getCatalogue(quote.mode);
  
  const eur = (v: number) =>
    new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v);

  const dateStr = new Date(quote.createdAt).toLocaleDateString('es-ES', { year: 'numeric', month: 'long', day: 'numeric' });

  return (
    <div className="hidden print:block absolute inset-0 bg-white text-black text-sm z-[9999] min-h-screen">
      {/* Page wrapper to enforce printing margins and page breaks */}
      <div className="max-w-[800px] mx-auto p-12 printable-content">
        
        {/* Header */}
        <div className="flex justify-between items-start border-b-2 border-slate-900 pb-6 mb-8 mt-4">
          <div>
            <h1 className="text-3xl font-black text-slate-900 tracking-tight">BUILD<span className="text-blue-600">PRO</span></h1>
            <p className="text-sm font-semibold text-slate-500 mt-1">Presupuesto de Ejecución Material</p>
          </div>
          <div className="text-right text-xs text-slate-600">
            <p className="font-bold text-slate-900 text-sm mb-1">CÓDIGO: {id.split('-')[0].toUpperCase()}</p>
            <p>Fecha: {dateStr}</p>
            <p>Tipo: {quote.tipoObra}</p>
          </div>
        </div>

        {/* Resumen & Memoria */}
        <div className="mb-8">
          <h2 className="text-lg font-bold text-slate-900 mb-2 border-b border-slate-200 pb-1">Resumen del Proyecto</h2>
          <p className="text-slate-700 leading-relaxed mb-4">{quote.resumen}</p>
          
          <div className="flex gap-12 text-sm">
            <div><span className="text-slate-500">Superficie:</span> <span className="font-semibold">{quote.superficie} m²</span></div>
            {quote.mode === 'nueva' && <div><span className="text-slate-500">Plantas:</span> <span className="font-semibold">{quote.plantas || '1'}</span></div>}
            <div><span className="text-slate-500">Nivel de Confianza IA:</span> <span className="font-semibold capitalize">{quote.confianza}</span></div>
          </div>
        </div>

        {quote.memoria && (
          <div className="mb-8 p-4 bg-slate-50 rounded-lg border border-slate-200 break-inside-avoid">
            <h3 className="font-bold text-slate-900 mb-2">Memoria Descriptiva</h3>
            <div className="text-xs text-slate-700 whitespace-pre-wrap leading-relaxed">
              {quote.memoria}
            </div>
          </div>
        )}

        {/* Partidas */}
        <div className="mb-8">
          <h2 className="text-lg font-bold text-slate-900 mb-4 border-b border-slate-200 pb-1">Desglose por Partidas</h2>
          
          {cats.map(cat => {
            const activeItems = quote.items.filter(it => it.id?.startsWith(cat.id + '_') || cat.items.some(ci => ci.id === it.id));
            if (activeItems.length === 0) return null;
            
            const catTotal = activeItems.reduce((acc, it) => acc + (it.total || 0), 0);

            return (
              <div key={cat.id} className="mb-6 break-inside-avoid">
                <div className="flex justify-between items-center bg-slate-100 px-3 py-1.5 font-bold text-slate-800 text-xs border border-slate-300">
                  <span>{cat.label.toUpperCase()}</span>
                  <span>{eur(catTotal)}</span>
                </div>
                <table className="w-full text-[11px] mt-2 border-b border-slate-200">
                  <thead>
                    <tr className="text-left text-slate-500 border-b border-slate-200">
                      <th className="py-1 w-16">CÓD</th>
                      <th className="py-1">DESCRIPCIÓN</th>
                      <th className="py-1 text-right w-16">CANT</th>
                      <th className="py-1 text-left w-12 pl-2">UD</th>
                      <th className="py-1 text-right w-20">PRECIO</th>
                      <th className="py-1 text-right w-20">TOTAL</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activeItems.map((it, i) => (
                      <tr key={it.id || i} className="border-b border-slate-100 last:border-0 text-slate-700">
                        <td className="py-1.5 align-top font-mono text-slate-400">{it.id?.split('_')[1] || it.id}</td>
                        <td className="py-1.5 pr-2 leading-tight">{it.name}</td>
                        <td className="py-1.5 align-top text-right font-semibold">{it.quantity % 1 === 0 ? it.quantity : it.quantity.toFixed(1)}</td>
                        <td className="py-1.5 align-top pl-2 text-slate-500">{it.unit}</td>
                        <td className="py-1.5 align-top text-right">{eur(it.price)}</td>
                        <td className="py-1.5 align-top text-right font-semibold text-slate-900">{eur(it.total || 0)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          })}
        </div>

        {/* Totals */}
        <div className="flex justify-end break-inside-avoid mt-8">
          <div className="w-64">
            <div className="flex justify-between py-1.5 text-slate-600 text-sm">
              <span>Base Imponible</span>
              <span>{eur(quote.totalExVat)}</span>
            </div>
            <div className="flex justify-between py-1.5 text-slate-600 text-sm border-b border-slate-300">
              <span>IVA (21%)</span>
              <span>{eur(quote.vat21 || (quote.totalExVat * 0.21))}</span>
            </div>
            <div className="flex justify-between py-3 text-slate-900 font-black text-lg">
              <span>TOTAL ESTIMADO</span>
              <span>{eur(quote.totalIncVat || (quote.totalExVat * 1.21))}</span>
            </div>
          </div>
        </div>

        {/* Footer info */}
        <div className="mt-12 pt-4 border-t border-slate-200 text-center text-slate-400 text-[10px] leading-relaxed break-inside-avoid">
          Este documento es una estimación de costes generada por inteligencia artificial base de datos de precios medios del mercado español (2024). 
          No constituye una oferta vinculante ni un proyecto técnico visado. Para la ejecución de las obras, es imprescindible solicitar presupuesto detallado y cerrado a contratistas homologados bajo la supervisión de un técnico competente.
        </div>
      </div>
    </div>
  );
};
